import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { IntervalCard } from '@/pages/aneel/IntervalCard';
import { fetchAneelNotifications } from '@/services/aneel';
import type { AneelBodyFunnel, AnswerOut } from '@/services/types';

/** The same floor the backend lists bodies above. */
const BODY_FLOOR = 20;

/**
 * The inspection funnel: the step upstream of every other ANEEL screen.
 *
 * Its trap is a zero that reads as a finding. None of the 707 inspections
 * resolved another way links to anything in this corpus — because no such
 * instrument is loaded. A screen showing "707 inspections, 0 became cases"
 * invites exactly one conclusion, and that conclusion is about the data pipeline
 * rather than the agency. So it is refused in its own panel rather than reported
 * as a count.
 *
 * The same shape has already been misread once in this product: a contradiction
 * model that had never run returned zero flagged pairs, and the zero read as a
 * clean bill of health.
 */
export function AneelNotificationsPage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['aneel', 'notifications'],
    queryFn: fetchAneelNotifications,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">
          {t('aneel:notifications.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('aneel:notifications.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="aneel:notifications.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/aneel/export/notifications" />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:notifications.funnel.title')}
        </h2>
        <Answer answer={data.funnel} help={t('aneel:notifications.funnel.help')}>
          {(funnel) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {(
                  [
                    ['notifications', funnel.notifications],
                    ['escalated', funnel.escalated],
                    ['escalatedLinked', funnel.escalated_linked],
                    ['other', funnel.other],
                  ] as const
                ).map(([key, value]) => (
                  <div key={key} className="rounded-md border bg-background p-3">
                    <dt className="text-xs text-muted-foreground">
                      {t(`aneel:notifications.funnel.${key}`)}
                    </dt>
                    <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">
                      {number.format(value)}
                    </dd>
                  </div>
                ))}
              </dl>
              {/* The escalation share, computed per request and until now
                  shipped without being rendered. Then both directions of the
                  link: a small number of escalated inspections name notices this
                  corpus lacks, and a small number of notices have no inspection
                  record — neither gap is assumed away. */}
              <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                <span data-escalation-share>
                  {t('aneel:notifications.funnel.escalation', {
                    escalated: number.format(funnel.escalated),
                    notifications: number.format(funnel.notifications),
                    share: percent.format(funnel.escalation_share),
                  })}
                </span>{' '}
                {t('aneel:notifications.funnel.bothWays', {
                  linked: number.format(funnel.escalated_linked),
                  escalated: number.format(funnel.escalated),
                  share: percent.format(funnel.link_share),
                  missing: number.format(funnel.cases_without_notification),
                  cases: number.format(funnel.total_cases),
                })}
              </p>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="aneel:notifications.guide.funnel" />
        </div>
      </section>

      {/* Its own panel. Reporting "0 of 707" as a count would state a finding
          about the agency out of an absence in the data pipeline. */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:notifications.otherTrack.title')}
        </h2>
        <Answer
          answer={data.other_track}
          help={t('aneel:notifications.otherTrack.help')}
          elsewhere={t('aneel:notifications.otherTrack.elsewhere')}
        >
          {/* Rendered for the day it answers: today it refuses, and a panel that
              rendered nothing on an answer would hide the moment that changes. */}
          {(coverage) => (
            <p className="text-sm text-foreground" data-other-linked={coverage.other_linked}>
              {t('aneel:notifications.otherTrack.answered', {
                linked: number.format(coverage.other_linked),
                other: number.format(coverage.other),
                share: percent.format(coverage.share),
              })}
            </p>
          )}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-notification-intervals>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:notifications.intervals.title')}
        </h2>
        <div className="space-y-3">
          {data.intervals.map((step) => (
            <IntervalCard
              key={step.stage}
              step={step}
              labelKey="aneel:notifications.intervals"
              impossibleKey="aneel:notifications.intervals.impossible"
            />
          ))}
        </div>
        <div className="mt-3">
          <FieldGuide i18nKey="aneel:notifications.guide.intervals" />
        </div>
      </section>

      <BodyFunnelPanel answer={data.by_body} />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:notifications.byYear.title')}
        </h2>
        <Answer answer={data.by_year} help={t('aneel:notifications.byYear.help')}>
          {(years) => (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">
                      {t('aneel:notifications.byYear.year')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:notifications.byYear.notifications')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:notifications.byYear.escalated')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:notifications.byYear.linked')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {years.map((row) => (
                    <tr key={row.year} className="border-t">
                      <td className="px-3 py-2 tabular-nums text-foreground">{row.year}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(row.notifications)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.escalated)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.linked)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Answer>
      </section>

      {data.by_nature.length > 0 ? (
        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-1 text-sm font-semibold text-foreground">
            {t('aneel:notifications.byNature.title')}
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">
            {t('aneel:notifications.byNature.help')}
          </p>
          <ul className="space-y-1">
            {data.by_nature.map((share) => (
              <li
                key={share.code}
                className="flex items-baseline justify-between gap-3 border-b pb-1 text-sm last:border-b-0"
              >
                <span className="text-foreground">{share.code}</span>
                {/* The share is of this nature's OWN inspections, so the count
                    beside it is the numerator and never the corpus. */}
                <span className="tabular-nums text-muted-foreground">
                  {number.format(share.count)} · {percent.format(share.share)}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <FieldGuide i18nKey="aneel:notifications.guide.byNature" />
          </div>
        </section>
      ) : null}
    </div>
  );
}

/**
 * The funnel per inspecting body. Alphabetical on the wire and rendered so;
 * the share is of each body's OWN inspections, and no row links anywhere --
 * the notification's body is not the notice's body, and the case list
 * filters by the latter.
 */
function BodyFunnelPanel({ answer }: { answer: AnswerOut<AneelBodyFunnel> }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="rounded-lg border bg-card p-4" data-by-body>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('aneel:notifications.byBody.title')}
      </h2>
      <Answer answer={answer} help={t('aneel:notifications.byBody.help')}>
        {(bodies) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('aneel:notifications.byBody.body')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:notifications.byBody.notifications')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:notifications.byBody.escalated')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:notifications.byBody.share')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:notifications.byBody.linked')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {bodies.rows.map((row) => (
                    <tr key={row.body} className="border-t" data-body={row.body}>
                      <td className="px-3 py-2 text-foreground">{row.body}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(row.notifications)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.escalated)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {percent.format(row.escalation_share)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.linked)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {bodies.suppressed_bodies > 0 ? (
              <p className="text-xs text-muted-foreground" data-suppressed-bodies>
                {t('aneel:notifications.byBody.suppressed', {
                  bodies: number.format(bodies.suppressed_bodies),
                  minimum: BODY_FLOOR,
                  notifications: number.format(bodies.suppressed_notifications),
                })}
              </p>
            ) : null}
            {bodies.unattributed > 0 ? (
              <p className="text-xs text-muted-foreground" data-unattributed>
                {t('aneel:notifications.byBody.unattributed', {
                  n: number.format(bodies.unattributed),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
      <div className="mt-3">
        <FieldGuide i18nKey="aneel:notifications.guide.byBody" />
      </div>
    </section>
  );
}
